// Globals
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getRegionSettlements, settlementToggleActive, settlementToggleHeader } from '../../feature';
import { regionsSelector } from '../../feature/selectors';

// Components
import {
  StyledPageTitle,
  StyledTabTitleWrapper,
  StyledContentBlock,
  StyledPageDescription,
  StyledTitleAndLinkWrapper,
  StyledLink,
} from '../../components/Styled';
import RegionSettlementsList from '../../components/RegionSettlementsList/RegionSettlementsList';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { ROUTES } from '@/models/router.model';

// Helpers
import { usePagination } from '@/hooks';
import { GetRegionSettlementsDTO } from '../../dtos';
import { RegionsAndPickupTabs } from '../../helpers/configs';

const RegionSettlementsContainer = () => {
  const { t } = useTranslation(['regions']);
  const dispatch = useAppDispatch();
  const { regionsSettlements, isLoading } = useAppState(regionsSelector);
  const { currentPage, onPageChange } = usePagination();
  const { id: regionId } = useParams();
  const { canManage } = usePermissions();

  useEffect(() => {
    if (!regionId) return;
    const dto = new GetRegionSettlementsDTO({ page: currentPage, region_id: regionId });
    dispatch(getRegionSettlements(dto));
  }, [currentPage]);

  const onStatusChange = (id: string) => {
    dispatch(settlementToggleActive(id));
  };

  const onInHeaderToggle = (id: string) => {
    dispatch(settlementToggleHeader(id));
  };

  return (
    <>
      <StyledTabTitleWrapper>
        <StyledTitleAndLinkWrapper>
          <StyledLink to={`${ROUTES.REGIONS_AND_PICKUP_INDEX}/${RegionsAndPickupTabs.REGIONS}`}>
            {t('back_to_all_regions')}
          </StyledLink>
        </StyledTitleAndLinkWrapper>
        <StyledPageTitle>{t('region_settlements_tab_title')}</StyledPageTitle>
        <StyledPageDescription>{t('region_settlements_tab_description')}</StyledPageDescription>
      </StyledTabTitleWrapper>
      <StyledContentBlock>
        <RegionSettlementsList
          isLoading={isLoading}
          currentPage={currentPage}
          onPageChange={onPageChange}
          total={regionsSettlements?.meta?.total ?? 0}
          regionsSettlements={regionsSettlements?.data ?? []}
          onStatusChange={onStatusChange}
          onInHeaderToggle={onInHeaderToggle}
          canManage={canManage(Resource.REGION)}
        />
      </StyledContentBlock>
    </>
  );
};

export default RegionSettlementsContainer;
